import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'

import { blogPostPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  getAllBlogSettings,
  getBlogLocale,
  getBlogPostPage,
} from '@lib/sanity/blog'
import { getPageSanityClient, getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'
import { useBlogCanonicalUrl } from '@lib/metadata'
import { getAllLocales, Locale } from '@lib/language'
import {
  CustomStaticPropsContext,
  PageType,
  StaticPathWithSlug,
} from '@lib/routes'
import { getBlogPostSchema } from '@lib/schema'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface BlogPostPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  blogLocale: Locale
  slug: string
  page: SanityBlogPostPage
  site: SanitySiteFragment
}

const BlogPostPage = ({
  draftMode,
  draftToken,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogPostPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl(
    PageType.BLOG_POST,
    page.slug,
    site.blogSettings
  )
  const blogPostSchema = getBlogPostSchema(page, site)

  if (draftMode && draftToken) {
    return (
      <Layout
        page={page}
        site={site}
        canonicalUrl={canonicalUrl}
        blogPostSchema={blogPostSchema}
        draftMode
      >
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={blogPostPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
              blogLocale,
              blogPostSlug: slug,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      blogPostSchema={blogPostSchema}
    >
      <Modules modules={page.modules} />
    </Layout>
  )
}

interface BlogPostPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogPostPageParams>
): Promise<GetStaticPropsResult<BlogPostPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const blogLocale = await getBlogLocale(sanityClient, context.locale)
  const slug = context.params?.slug ?? ''
  const blogPostPage = await getBlogPostPage(
    sanityClient,
    context.locale,
    blogLocale,
    slug
  )

  if (!blogPostPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      blogLocale,
      slug,
      page: blogPostPage.page,
      site: blogPostPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogPostPageParams>
> {
  const sanityClient = getSanityClient()
  const blogPosts = await getAllSlugs(sanityClient, 'blogPost')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogPosts.forEach((blogPost) => {
    const locale = blogPost.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({
        params: {
          slug: blogPost.slug,
        },
        locale,
      })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({
          params: {
            slug: blogPost.slug,
          },
          locale: childLocale,
        })
      }
    }
  })

  return { paths, fallback: 'blocking' }
}

export default BlogPostPage
